import { ErrorBoundary, useBreakpoint, useIntersection } from '@buzzfeed/react-components';
import SkipToContent from '@buzzfeed/react-components/lib/components/SkipToContent';
import { captureException } from '@sentry/nextjs';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useRef, useState, useEffect } from 'react';
import DestinationContext from '../../../contexts/destination';
import dynamicImport from '../../../utils/dynamic-import';
import { getUserInfo } from '@buzzfeed/bf-utils/lib/user';
import { isNews } from '../../../utils/isNews';
import { isGeneratorPage } from '../../../utils/isGeneratorPage';
import { hasDisableBackfillPlayerTag } from '../../../utils/connatixVideo';
import { isGamesPost } from '../../../utils/games';
import { ActionBar } from '../../ActionBar';
import CommentsWrapper from '../../CommentsWrapper';
import AdUnit from '../../Ads/AdUnit';
import AdInlineTrending from '../../Ads/components/AdInlineTrending';
import AmazonOneLink from '../../AmazonOneLink';
import CommerceSEOLinks from '../../CommerceSEOLinks';
import CommerceDisclaimer from '../../CommerceDisclaimer';
import ContentBottom from '../../ContentBottom';
import FooterCommunity from '../../FooterCommunity';
import Headline from '../../Headline';
import BuzzNewsFooter from '../../NewsFooter';
import NewsletterSignup from '../../NewsletterSignup/lazy';
import Package from '../../Package/lazy';
import { PageOverlay } from '../../PageOverlay';
import PubFeed from '../../PubFeed';
import MobileAppPromo from '../../MobileAppPromo';
import Reactions from '../../Reactions/lazy';
import ReactionsAnimated from '../../ReactionsAnimated/lazy';
import { BottomRecircList, RelatedRecircList } from '../../RecircList/variants';
import Sidebar from '../../Sidebar';
import StudiosPromoUnit from '../../StudiosPromoUnit';
import Subbuzzes from '../../Subbuzzes';
import TakeQuizYourself from '../../TakeQuizYourself';
import TaboolaJP from '../../Taboola/JP';
import TaboolaAPAC from '../../Taboola/APAC';
import TaboolaAU from '../../Taboola/AU';
import TaboolaIDNML from '../../Taboola/IDNML';
import TopicTags from '../../TopicTags';
import TrendingPosts from '../../TrendingPosts';
import TrendingProducts from '../../TrendingProducts';
import TableOfContents from '../../TableOfContents';
import TrendingShoppingCarousel from '../../TrendingShoppingCarousel/lazy';
import GooglePreferredSourcesButton from '../../GooglePreferredSourcesButton';
import layoutStyles from '../post.module.scss';
import ABeagleContext from "../../../contexts/abeagle";
import ElementsPlayer from '../../Connatix/Elements';
import ShareBarBottom from '../../ShareBarBottom';
import SubbbuzzSharedModal from '../../SubbuzzSharedModal';
import InfiniteScroll from '../../InfiniteScroll';
import ScrollCueBox from '../../InfiniteScroll/ScrollCueBox';
import { PACKAGE_VISIBILITY_OBSERVER_CONFIG } from '../../../constants/intersection-observer';
import { isIDNML } from '../../../utils/idnml';
import HeadlineByline from '../../Headline/components/HeadlineByline';
import PostHead from '../../PostHead';

const WalmartBundle = dynamicImport(
  () => import(/* webpackChunkName: "walmart-promo" */ '../../WalmartBundle'),
  { ssr: false }
);

const QuizResultConfetti = dynamicImport(
  () => import(/* webpackChunkName: "quiz-result-confetti" */ '../../QuizResultConfetti'),
  { ssr: false }
);

function DefaultPost({
  buzz,
  subbuzzData,
  badges,
  showOverlay,
  setShowOverlay,
}) {
  const ref = useRef(null);
  const { isIntersecting: isFloatingButtonsEnd, setObservable: setFloatingButtonsEnd } = useIntersection({ once: true });
  const { destination } = useContext(DestinationContext);
  const isMobile = useBreakpoint('(max-width:500px)'); // page level break is based on bf_header_ui break
  const isDesktop = useBreakpoint('(min-width: 64rem)');
  const showConfettiWithReactions = buzz.isTrivia || buzz.isPersonality;
  const showTopShareBar =
    (!(buzz.shouldHideBuzzSharing || buzz.shouldShowPostContentOnly) &&
      !isMobile) ||
    isNews(buzz);
  const [commentCount, setCommentCount] = useState(null);
  const { getFeatureFlagValue, experiments, getExperimentValue } = useContext(ABeagleContext);
  const isGames = isGamesPost(buzz);
  const [shoppingInfiniteScrollV2, setShoppingInfiniteScrollV2] = useState(false);
  const [topVideoPlayerQuiz, setTopVideoPlayerQuiz] = useState(false);
  const [taboolaAPACFF, setTaboolaAPAC] = useState(false);
  const [taboolaAUFF, setTaboolaAU] = useState(false);
  // 1710959400 - Date and time (GMT): Wednesday, March 20, 2024 7:30:00 PM GMT+01:00
  const showEmojiReactions = !!(buzz.published && parseInt(buzz.published, 10) > 1710959400);
  const [isShowSharedSubbuzzModal, setIsShowSharedSubbuzzModal] = useState(false);
  const sharedSubbubbuzzId = buzz?.metadata?.subbuzzId;
  const [hideAdsInSubbuzzes, setHideAdsInSubbuzzes] = useState(false);
  const isBottomAdEligible = (buzz.isQuiz || isGames) ? isIDNML({ isBFN: isNews(buzz) }) : true;
  const isUS = buzz.country_code === 'en-us';
  const isUSOrCA = isUS || buzz.country_code === 'en-ca';
  const showPostHead = !isNews(buzz)
    && !isGeneratorPage(buzz)
    && buzz.editorial_status !== 'Advertising'
    && (isUS || !buzz.isShopping) // non-US preserves original behavior: no PostHead for shopping
    && isUSOrCA;

  // Show Shared Subbuzz Modal when subbuzzid found in buzz.metadata
  useEffect(() => {
    if (sharedSubbubbuzzId) {
      setIsShowSharedSubbuzzModal(true);
    }
  }, [sharedSubbubbuzzId]);

  useEffect(() => {
    const variant = getExperimentValue('ads_shopping_infinite_scroll_v2', { rejectErrors: false });
    if (variant === 'is_v2') setShoppingInfiniteScrollV2(true);
  }, [getExperimentValue, buzz]);

  useEffect(() => {
    if (experiments && experiments.loaded && !hasDisableBackfillPlayerTag(buzz)) {
      setTopVideoPlayerQuiz(
        getFeatureFlagValue('RT-1378-us-quiz-top-sticky-desktop') ||
        getFeatureFlagValue('ads-quiz-top-player-mobile') ||
        getExperimentValue('RT-2424-sticky-connatix-desktop-games', { rejectErrors: false }) === 'on'
      );
    }
  }, [experiments, getFeatureFlagValue, buzz]);

  useEffect(() => {
    if (experiments && experiments.loaded) {
      const variant = getFeatureFlagValue('taboola_apac');
      setTaboolaAPAC(variant);
      const variant_au = getFeatureFlagValue('taboola_au');
      setTaboolaAU(variant_au);
    }
  }, [experiments, getFeatureFlagValue]);

  const onOverlayClick = useCallback(() => {
    setShowOverlay(false);
  }, [setShowOverlay]);

  // Infinite Scroll state management
  const isSafari = typeof window !== 'undefined' &&
    /Safari/.test(navigator.userAgent) &&
    !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR/.test(navigator.userAgent);

  const [hideStatic, setHideStatic] = useState(false);
  const [activateInfinite, setActivateInfinite] = useState(false);
  const [hideDefaultScrollCude, setHideDefaultScrollCude] = useState(false);
  const [sidebarShouldStop, setSidebarShouldStop] = useState(false);
  const packageRef = useRef(null);
  const isMounted = useRef(true);

  // Infinite Scroll event handlers
  const handleExpandInfinite = useCallback(() => {
    if (!activateInfinite) setActivateInfinite(true);
  }, [activateInfinite]);

  const handleFirstInfiniteLoad = useCallback(() => {
    if (hideStatic) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!isMounted.current) return;
        setHideStatic(true);
      });
    });
  }, [hideStatic]);

  const handleScrollCueExpand = useCallback(() => {
    setHideAdsInSubbuzzes(true);
    handleExpandInfinite();
    if (isSafari) handleFirstInfiniteLoad();
  }, [isSafari, handleExpandInfinite, handleFirstInfiniteLoad]);

  const handleScrollCueOffscreen = useCallback(() => {
    if (!isSafari) handleFirstInfiniteLoad();
  }, [isSafari, handleFirstInfiniteLoad]);

  // Component lifecycle
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fix for DOM manipulation errors in infinite scroll
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function (child) {
      try {
        return originalRemoveChild.call(this, child);
      } catch (err) {
        return null;
      }
    };

    return () => {
      Node.prototype.removeChild = originalRemoveChild;
    };
  }, []);

  // Sidebar stop behavior: Monitor Package visibility on initial page
  useEffect(() => {
    if (!packageRef.current || typeof window === 'undefined' || !shoppingInfiniteScrollV2) return;

    const element = packageRef.current; // Capture ref value for cleanup
    const observer = new IntersectionObserver(
      ([entry]) => setSidebarShouldStop(entry.isIntersecting),
      PACKAGE_VISIBILITY_OBSERVER_CONFIG
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [shoppingInfiniteScrollV2, hideStatic]);

  // Sidebar stop behavior: Listen for Package visibility from infinite scroll posts
  useEffect(() => {
    if (typeof window === 'undefined' || !shoppingInfiniteScrollV2) return;

    const handlePackageVisible = (event) => setSidebarShouldStop(event.detail.isVisible);
    window.addEventListener('infiniteScrollPackageVisible', handlePackageVisible);
    return () => window.removeEventListener('infiniteScrollPackageVisible', handlePackageVisible);
  }, [shoppingInfiniteScrollV2]);

  const renderContentBetweenTaboola = () => (
    <>
      {taboolaAPACFF && (
        <TaboolaAPAC />
      )}
      <ShareBarBottom buzz={buzz} />
      {!buzz.isShopping && (
        <div>
          {!showEmojiReactions && (showConfettiWithReactions ? <ReactionsAnimated /> : <Reactions />)}
          <TaboolaIDNML />
          {!getUserInfo() && !isGames && (
            <Package
              style={{ marginBottom: '3rem' }}
              isNewDesign={true}
              isShoppingDesign={false}
            />
          )}
          <CommentsWrapper
            buzz={buzz}
            commentCount={commentCount}
            onCommentsLoaded={setCommentCount}
            firstPage={100}
            repliesFirstPage={2}
            perPage={100}
            showEmojiReactions={showEmojiReactions}
          />
          <NewsletterSignup />
        </div>
      )}
      {buzz.isShopping && (
        <TaboolaIDNML />
      )}
      <PubFeed />
      {taboolaAUFF && <TaboolaAU />}
      {!isNews(buzz) && !buzz.isShopping && isBottomAdEligible && (
          <AdUnit
            type="story-bpage-desktop"
            pixiedust={{
              unit_type: 'buzz_bottom',
              unit_name: buzz.id,
              position_in_unit: 1,
            }}
            style={{ marginTop: '-2rem' }}
          />
        )
      }
      <FooterCommunity />
      {isMobile && !buzz.isShopping && !shoppingInfiniteScrollV2 && isBottomAdEligible && (
        <AdUnit
          type="bigstory"
          pixiedust={{
            unit_type: 'buzz_bottom',
            unit_name: buzz.id,
            position_in_unit: 1,
          }}
        />
      )}
      {showConfettiWithReactions && <QuizResultConfetti />}
    </>
  );

  return (
    <main
      aria-hidden={isShowSharedSubbuzzModal}
      id="buzz-content"
      className={`${layoutStyles.main} ${layoutStyles[destination]} refreshed_bpage embed-content`}
      ref={ref}
    >
      <ErrorBoundary onError={captureException}>
        <AmazonOneLink buzz={buzz} />
      </ErrorBoundary>
      <SubbbuzzSharedModal
        buzz={buzz}
        isOpen={isShowSharedSubbuzzModal}
        key={`shared-modal-${buzz.id}`}
        onRequestClose={() => setIsShowSharedSubbuzzModal(false)}
        subbuzzData={subbuzzData}
        subbuzzId={sharedSubbubbuzzId}
      />
      <article>
        {showPostHead && (
          <>
            {isUS && <CommerceDisclaimer buzz={buzz} />}
            <PostHead
              buzz={buzz}
              badges={badges}
              commentCount={commentCount}
              showShareBar={showTopShareBar}
              destination={destination}
              isFloatingButtonsEnd={isFloatingButtonsEnd}
            />
          </>
        )}
        <div
          className={`${layoutStyles.article} ${layoutStyles.default} ${layoutStyles[destination]} embed-post`}
        >
          <div className={layoutStyles.content}>
            {!hideStatic && (
              <>
                {!showPostHead && (
                  <>
                    <Headline buzz={buzz} badges={badges} />
                    <ActionBar
                      buzz={buzz}
                      commentCount={commentCount}
                      showShareBar={showTopShareBar}
                      destination={destination}
                      isFloatingButtonsEnd={isFloatingButtonsEnd}
                    />
                  </>
                )}
                {showPostHead && (
                  <HeadlineByline
                    bylines={buzz.bylines}
                    isAd={buzz.flags.ad === 1}
                    buzz={buzz}
                    designUpdateEnabled
                  />
                )}
                <GooglePreferredSourcesButton buzz={buzz} />
                <TrendingProducts buzz={buzz} postRef={ref} />
                <TableOfContents buzz={buzz} />
                {buzz.tags.some((tag) =>
                  ['etsy-strategy', 'etsy_strategy', 'etsy strategy'].includes(tag)
                ) && (
                  <AdInlineTrending
                    pixiedust={{
                      unit_type: 'buzz_body',
                      unit_name: buzz.id,
                      position_in_unit: 0,
                    }}
                  />
                )}
                <MobileAppPromo />
                {topVideoPlayerQuiz &&
                  <ElementsPlayer buzz={buzz} showOnTopQuiz={true} />}
                <Subbuzzes
                  buzz={buzz}
                  key={buzz.id}
                  subbuzzData={subbuzzData}
                  hideAds={hideAdsInSubbuzzes}
                  pixiedust={{
                    unit_type: 'buzz_body',
                    unit_name: buzz.id,
                    position_in_unit: 0,
                  }}
                />
                {buzz.hasStudiosPromoUnit && <StudiosPromoUnit buzz={buzz} />}
                {isNews(buzz) && <TopicTags buzz={buzz} />}
                <CommerceSEOLinks buzz={buzz} />
                <BuzzNewsFooter buzz={buzz} />
                <ContentBottom buzz={buzz} />
                {!isDesktop && buzz.walmartBundle && <WalmartBundle buzz={buzz} />}
              </>
            )}

            {/* Infinite Scroll v2: Full-width Package + ScrollCueBox */}
            {buzz.isShopping && shoppingInfiniteScrollV2 && !hideStatic && !isGames && (
              <div ref={packageRef} className={layoutStyles.infiniteScrollPackageWrapper}>
                <Package
                  isNewDesign={true}
                  isShoppingDesign={true}
                  limitedItems={isMobile ? 2 : 3}
                />
              </div>
            )}

            {buzz.isShopping && shoppingInfiniteScrollV2 && (
              <>
                {!hideDefaultScrollCude && (
                  <ScrollCueBox
                    onExpandComplete={handleScrollCueExpand}
                    onOffscreenScrollDown={handleScrollCueOffscreen}
                    previousPath={buzz.canonical_path || buzz.canonical_url}
                  />
                )}
                {activateInfinite && (
                  <InfiniteScroll
                    disableDefaultIS={setHideDefaultScrollCude}
                  />
                )}
              </>
            )}
            <ErrorBoundary onError={captureException}>
              <TakeQuizYourself buzz={buzz} />
              <AdUnit
                type="subbuzz"
                pixiedust={{
                  unit_type: 'buzz_bottom',
                  unit_name: buzz.id,
                  position_in_unit: 0,
                }}
              />
              {buzz.classification.edition === 'Japan' && (
                <TaboolaJP isMobile={isMobile}/>
              )}
              {/* RT-1950: BFJ Taboola split feed */}
              {buzz.classification.edition === 'Japan' ? (
                <div id="taboola-split-01">
                  {renderContentBetweenTaboola()}
                </div>
              ) : (
                renderContentBetweenTaboola()
              )}
            </ErrorBoundary>
          </div>
          <Sidebar
            key={buzz?.id}
            pixiedust={{
              unit_type: 'sidebar',
              unit_name: 'right',
              position_in_unit: 0,
            }}
            shouldStopSticky={sidebarShouldStop}
          />
        </div>
      </article>
      <ErrorBoundary onError={captureException}>
        {/* Show package for shopping pages in control (not v2 experiment) and for non-shopping pages with authenticated users */}
        {((!shoppingInfiniteScrollV2 && buzz.isShopping) || (!buzz.isShopping && getUserInfo())) &&
          !isGames &&
          <Package
            style={{ marginTop: isMobile ? '1rem' : '-2rem', marginBottom: '3rem' }}
            isNewDesign={true}
            isShoppingDesign={buzz.isShopping}
            variant={(showPostHead || buzz.isQuiz || isGames)  ? 'refreshed' : ''}
          />
        }
        {/*ToDo: add Ads slot*/}
        <RelatedRecircList
          listLayout={buzz.isShopping  ? 'gridNarrow' : 'grid'}
          className={(showPostHead || buzz.isQuiz || isGames) ? 'refreshed' : ''}
          variant={showPostHead ? 'wavesSection' : 'bottomUnit'}
        />
        { buzz.isShopping && buzz.classification.edition !== 'Japan' && !shoppingInfiniteScrollV2 && <TrendingPosts /> }
        { !buzz.isShopping && <TrendingShoppingCarousel /> }
        {buzz.classification.edition === 'Japan' && (
          <TaboolaJP placement={2} isMobile={isMobile}/>
        )}
        {isNews(buzz) && <SkipToContent jumpTo="news-footer" className="skipToFooter" label="Skip to footer" />}
        {/*ToDo: add Ads slot*/}
        {!shoppingInfiniteScrollV2 && <BottomRecircList className={showPostHead || buzz.isQuiz || isGames ? 'refreshed' : ''} />}
        <div ref={setFloatingButtonsEnd}></div>
      </ErrorBoundary>
      {showOverlay && <PageOverlay onOverlayClick={onOverlayClick} />}
    </main>
  );
}

DefaultPost.propTypes = {
  buzz: PropTypes.object.isRequired,
  badges: PropTypes.array,
  subbuzzData: PropTypes.shape({
    assets: PropTypes.object,
    subbuzzes: PropTypes.array,
    gateIndex: PropTypes.number,
  }),
};

export default DefaultPost;
